// /leadership/ layouts from [leadership_people] (lib/shortcodes/leadership-people.php):
//   .leaders-section  headings rise in word by word, and each layout enters, when first seen
//   .leaders-panels   the portrait gallery keeps the last hovered, focused or tapped panel open
//   .leaders-graph    on wide screens the people ride a tilted orbit around a Neo4j hub; the
//                     front one is selected, its details beside the orbit, and the orbit moves
//                     to the next person every few seconds (drag to spin, Pause to stop)
// All of it works without this script (the gallery opens on hover, the graph is a list). Bios
// open through leadership-modal.js. With reduced motion nothing enters or turns by itself.
(function () {
	var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	var TAU = Math.PI * 2;
	// Only a pointer that moves selects: content scrolling under a resting cursor (which fires
	// mouseenter) must not change what is open.
	var moved = function (handler) {
		return function (e) {
			if (e.pointerType === 'mouse' && (e.movementX || e.movementY)) { handler(); }
		};
	};

	/* Section entrances */
	if (!reduced && 'IntersectionObserver' in window) {
		var reveal = new IntersectionObserver(function (entries) {
			entries.forEach(function (entry) {
				if (!entry.isIntersecting) { return; }
				entry.target.classList.add('is-in');
				reveal.unobserve(entry.target);
			});
		}, { threshold: 0.12 });
		document.querySelectorAll('.leaders-section').forEach(function (section) {
			var heading = section.querySelector('h2');
			if (heading && heading.children.length === 0) {
				var words = heading.textContent.trim().split(/\s+/);
				heading.setAttribute('aria-label', heading.textContent.trim());
				heading.textContent = '';
				words.forEach(function (word, i) {
					var outer = document.createElement('span');
					var inner = document.createElement('span');
					outer.className = 'leaders-word';
					outer.setAttribute('aria-hidden', 'true');
					inner.style.setProperty('--i', i);
					inner.textContent = word;
					outer.appendChild(inner);
					heading.appendChild(outer);
					if (i < words.length - 1) { heading.appendChild(document.createTextNode(' ')); }
				});
			}
			section.querySelectorAll('.leaders-panel, .leaders-index__item').forEach(function (el, i) {
				el.style.setProperty('--i', i);
			});
			section.classList.add('is-anim');
			reveal.observe(section);
		});
	}

	/* Portrait gallery */
	document.querySelectorAll('.leaders-panels').forEach(function (gallery) {
		var panels = Array.prototype.slice.call(gallery.querySelectorAll('.leaders-panel'));
		gallery.classList.add('is-enhanced');
		panels.forEach(function (panel) {
			var open = function () {
				panels.forEach(function (other) { other.classList.toggle('is-open', other === panel); });
			};
			panel.addEventListener('pointermove', moved(open));
			panel.addEventListener('focusin', open);
			// A tap opens a closed panel; a click anywhere on an open one opens the bio.
			panel.addEventListener('click', function (e) {
				if (e.target.closest('a, button')) { return; }
				if (!panel.classList.contains('is-open')) {
					open();
					return;
				}
				var bio = panel.querySelector('[data-leader-open]');
				if (bio) { bio.click(); }
			});
		});
	});

	/* The board as an orbit */
	document.querySelectorAll('.leaders-graph').forEach(function (graph) {
		var stage = graph.querySelector('.leaders-graph__stage');
		var hub = graph.querySelector('.leaders-graph__hub');
		var panel = graph.querySelector('.leaders-graph__panel');
		var items = Array.prototype.slice.call(graph.querySelectorAll('.leaders-node'));
		var canvas = document.createElement('canvas');
		var ctx = canvas.getContext && canvas.getContext('2d');
		if (!stage || !hub || !panel || items.length < 3 || !ctx || !window.matchMedia) { return; }

		var DWELL = 6000;
		var wide = window.matchMedia('(min-width: 960px)');
		var root = window.getComputedStyle(document.documentElement);
		var token = function (slug, fallback) {
			var value = root.getPropertyValue('--wp--preset--color--' + slug).trim();
			var hex = (/^#[0-9a-f]{6}$/i.test(value) ? value : fallback).slice(1);
			return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)];
		};
		var rgba = function (c, a) { return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + Math.max(0, Math.min(1, a)).toFixed(3) + ')'; };
		var TEAL = token('baltic-20', '#8fe3e8');
		var AMBER = token('marigold-20', '#ffcf72');
		var INK = token('baltic-80', '#01121c');
		var FONT = '500 11px "Fira Code", ui-monospace, monospace';

		var n = items.length, step = TAU / n;
		var nodes = items.map(function (item, i) { return { item: item, a: i * step, rel: ':' + (item.getAttribute('data-rel') || 'MEMBER_OF') }; });
		var W = 0, H = 0, dpr = 1, cx = 0, cy = 0, rx = 0, ry = 0, hubR = 64, photoR = 52, on = false;
		var rot = -TAU * 0.35, target = 0, spread = reduced ? 1 : 0, spreadTarget = reduced ? 1 : 0;
		var selected = -1, elapsed = 0, last = 0, raf = 0, onScreen = false, engaged = false, stopped = reduced;
		var drag = null, suppressClick = false, pulses = [], lastPulse = 0;

		// The details and the controls share the left column.
		var aside = document.createElement('div');
		var controls = document.createElement('div');
		aside.className = 'leaders-graph__aside';
		controls.className = 'leaders-graph__controls';
		controls.innerHTML = '<button type="button" class="leaders-graph__step" data-step="-1" aria-label="Previous person"><span aria-hidden="true">&larr;</span></button>' +
			'<span class="leaders-graph__count" aria-hidden="true"></span>' +
			'<button type="button" class="leaders-graph__step" data-step="1" aria-label="Next person"><span aria-hidden="true">&rarr;</span></button>' +
			'<button type="button" class="leaders-graph__toggle"><span class="leaders-graph__icon" aria-hidden="true"></span><span class="show-for-sr"></span></button>';
		graph.insertBefore(aside, panel);
		aside.appendChild(panel);
		aside.appendChild(controls);
		var count = controls.querySelector('.leaders-graph__count');
		var toggle = controls.querySelector('.leaders-graph__toggle');
		var label = function () {
			toggle.lastChild.textContent = stopped ? 'Play' : 'Pause';
			toggle.setAttribute('aria-pressed', stopped ? 'true' : 'false');
			toggle.classList.toggle('is-stopped', stopped);
		};
		label();

		canvas.className = 'leaders-graph__edges';
		canvas.setAttribute('aria-hidden', 'true');
		stage.insertBefore(canvas, stage.firstChild);

		function pad(v) { return (v < 10 ? '0' : '') + v; }

		function select(index) {
			index = ((index % n) + n) % n;
			// The shortest way round to bring this person to the front.
			var t = -nodes[index].a;
			target = t + TAU * Math.round((rot - t) / TAU);
			elapsed = 0;
			if (index === selected) { return wake(); }
			selected = index;
			items.forEach(function (item, i) { item.classList.toggle('is-selected', i === index); });
			var detail = items[index].querySelector('.leaders-node__detail');
			panel.textContent = '';
			if (detail) {
				var copy = detail.cloneNode(true);
				copy.classList.add('is-entering');
				panel.appendChild(copy);
			}
			count.textContent = pad(index + 1) + ' / ' + pad(n);
			wake();
		}

		function layout() {
			on = wide.matches;
			graph.classList.toggle('is-graph', on);
			if (!on) {
				items.forEach(function (item) {
					item.style.transform = '';
					item.style.zIndex = '';
					item.style.removeProperty('--d');
				});
				return;
			}
			var box = stage.getBoundingClientRect();
			W = box.width;
			H = box.height;
			dpr = Math.min(window.devicePixelRatio || 1, 2);
			canvas.width = Math.round(W * dpr);
			canvas.height = Math.round(H * dpr);
			hubR = hub.offsetWidth / 2;
			var photo = items[0].querySelector('.leaders-node__photo');
			photoR = photo ? photo.offsetWidth / 2 : 52;
			// Open enough that the front person clears the hub with room for their relationship.
			cx = W / 2;
			cy = H * 0.4;
			rx = W / 2 - photoR - 30;
			ry = Math.min(H * 0.34, Math.max(rx * 0.5, hubR + photoR * 1.12 + 56));
			if (selected < 0) { select(0); }
			place();
			draw(window.performance.now());
		}

		// Depth runs from -1 (the back of the ring) to 1 (the front).
		function place() {
			nodes.forEach(function (node, i) {
				var th = node.a + rot, d = Math.cos(th), f = (d + 1) / 2;
				var r = spread;
				node.x = cx + Math.sin(th) * rx * r;
				node.y = cy + Math.cos(th) * ry * r;
				node.d = d;
				node.s = (0.5 + 0.62 * f) * (0.4 + 0.6 * r);
				node.item.style.transform = 'translate(' + node.x.toFixed(1) + 'px,' + node.y.toFixed(1) + 'px) scale(' + node.s.toFixed(3) + ')';
				node.item.style.zIndex = String(Math.round(f * 100));
				node.item.style.setProperty('--d', d.toFixed(3));
				node.item.style.setProperty('--f', i === selected ? '1' : f.toFixed(3));
			});
		}

		function pill(text, x, y) {
			var w = ctx.measureText(text).width + 18, h = 22, r = 11;
			ctx.beginPath();
			ctx.moveTo(x - w / 2 + r, y - h / 2);
			ctx.arcTo(x + w / 2, y - h / 2, x + w / 2, y + h / 2, r);
			ctx.arcTo(x + w / 2, y + h / 2, x - w / 2, y + h / 2, r);
			ctx.arcTo(x - w / 2, y + h / 2, x - w / 2, y - h / 2, r);
			ctx.arcTo(x - w / 2, y - h / 2, x + w / 2, y - h / 2, r);
			ctx.closePath();
			ctx.fillStyle = rgba(INK, 0.92);
			ctx.fill();
			ctx.strokeStyle = rgba(AMBER, 0.7);
			ctx.lineWidth = 1;
			ctx.stroke();
			ctx.fillStyle = rgba(AMBER, 1);
			ctx.fillText(text, x, y + 0.5);
		}

		function draw(now) {
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
			ctx.clearRect(0, 0, W, H);
			ctx.font = FONT;
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';

			// The orbit itself, brighter at the front.
			var grad = ctx.createLinearGradient(0, cy - ry, 0, cy + ry);
			grad.addColorStop(0, rgba(TEAL, 0.06));
			grad.addColorStop(1, rgba(TEAL, 0.32));
			ctx.strokeStyle = grad;
			ctx.lineWidth = 1;
			ctx.beginPath();
			ctx.ellipse(cx, cy, Math.max(1, rx * spread), Math.max(1, ry * spread), 0, 0, TAU);
			ctx.stroke();
			ctx.strokeStyle = rgba(TEAL, 0.05);
			ctx.beginPath();
			ctx.ellipse(cx, cy, Math.max(1, rx * spread * 1.12), Math.max(1, ry * spread * 1.16), 0, 0, TAU);
			ctx.stroke();

			var settled = Math.abs(target - rot) < 0.02 && spread > 0.98;
			nodes.forEach(function (node, i) {
				var dx = cx - node.x, dy = cy - node.y, dist = Math.sqrt(dx * dx + dy * dy) || 1, ux = dx / dist, uy = dy / dist;
				var sx = node.x + ux * (photoR * node.s + 6), sy = node.y + uy * (photoR * node.s + 6);
				var ex = cx - ux * (hubR + 10), ey = cy - uy * (hubR + 10);
				if ((ex - sx) * ux + (ey - sy) * uy <= 0) { return; }
				var front = i === selected, f = (node.d + 1) / 2;
				node.sx = sx;
				node.sy = sy;
				node.ex = ex;
				node.ey = ey;
				ctx.strokeStyle = front ? rgba(AMBER, 0.9) : rgba(TEAL, 0.08 + 0.3 * f);
				ctx.lineWidth = front ? 1.6 : 1;
				ctx.beginPath();
				ctx.moveTo(sx, sy);
				ctx.lineTo(ex - ux * 6, ey - uy * 6);
				ctx.stroke();
				ctx.fillStyle = ctx.strokeStyle;
				ctx.beginPath();
				ctx.moveTo(ex, ey);
				ctx.lineTo(ex - ux * 7 - uy * 3.5, ey - uy * 7 + ux * 3.5);
				ctx.lineTo(ex - ux * 7 + uy * 3.5, ey - uy * 7 - ux * 3.5);
				ctx.closePath();
				ctx.fill();
			});

			pulses.forEach(function (p) {
				var node = nodes[p.i];
				if (node.sx === undefined) { return; }
				var x = node.sx + (node.ex - node.sx) * p.t, y = node.sy + (node.ey - node.sy) * p.t;
				var glow = p.i === selected ? AMBER : TEAL, fade = Math.sin(Math.PI * p.t) * (0.3 + 0.7 * (node.d + 1) / 2);
				ctx.fillStyle = rgba(glow, 0.22 * fade);
				ctx.beginPath();
				ctx.arc(x, y, 6, 0, TAU);
				ctx.fill();
				ctx.fillStyle = rgba(glow, fade);
				ctx.beginPath();
				ctx.arc(x, y, 2.2, 0, TAU);
				ctx.fill();
			});

			// The front person's relationship, read like Neo4j Browser draws it.
			var sel = nodes[selected];
			if (settled && sel && sel.sx !== undefined) {
				pill(sel.rel, (sel.sx + sel.ex) / 2, (sel.sy + sel.ey) / 2);
			}
		}

		function frame(now) {
			raf = 0;
			if (!on) { return; }
			var dt = last ? Math.min(64, now - last) : 16;
			last = now;
			var k = dt / 16.667;
			if (!drag) { rot += (target - rot) * (reduced ? 1 : 1 - Math.pow(0.91, k)); }
			spread += (spreadTarget - spread) * (reduced ? 1 : 1 - Math.pow(0.95, k));
			if (Math.abs(spreadTarget - spread) < 0.001) { spread = spreadTarget; }
			var settled = Math.abs(target - rot) < 0.002 && spread === spreadTarget;
			if (settled && !drag) { rot = target; }

			if (!stopped && !engaged && !drag && onScreen && spread === 1) {
				elapsed += dt;
				if (elapsed >= DWELL) { select(selected + 1); }
			}
			if (!reduced && spread > 0.9) {
				if (now - lastPulse > 380) {
					pulses.push({ i: Math.random() < 0.4 ? selected : Math.floor(Math.random() * n), t: 0 });
					lastPulse = now;
				}
				pulses.forEach(function (p) { p.t += 0.011 * k; });
				pulses = pulses.filter(function (p) { return p.t < 1; });
			}
			place();
			draw(now);
			if (onScreen && (!reduced || !settled || drag)) {
				raf = window.requestAnimationFrame(frame);
			} else {
				last = 0;
			}
		}

		function wake() {
			if (!raf && on) { raf = window.requestAnimationFrame(frame); }
		}

		function indexOf(el) {
			var item = el && el.closest ? el.closest('.leaders-node') : null;
			return item ? items.indexOf(item) : -1;
		}

		// A click on someone at the back brings them round instead of opening their bio.
		stage.addEventListener('click', function (e) {
			var i = indexOf(e.target);
			if (suppressClick || (i >= 0 && i !== selected)) {
				e.preventDefault();
				e.stopPropagation();
				if (!suppressClick && i >= 0) { select(i); }
			}
			suppressClick = false;
		}, true);

		stage.addEventListener('pointerdown', function (e) {
			if (!on || e.button !== 0) { return; }
			drag = { x: e.clientX, rot: rot, moved: false, id: e.pointerId };
		});
		stage.addEventListener('pointermove', function (e) {
			if (e.pointerType === 'mouse' && (e.movementX || e.movementY)) { engaged = true; }
			if (!drag) { return; }
			var dx = e.clientX - drag.x;
			if (!drag.moved && Math.abs(dx) > 6) {
				drag.moved = true;
				try { stage.setPointerCapture(drag.id); } catch (err) { /* capture is a nicety */ }
				stage.classList.add('is-dragging');
			}
			if (drag.moved) {
				rot = drag.rot + dx / rx * 1.4;
				wake();
			}
		});
		var release = function () {
			if (!drag) { return; }
			if (drag.moved) {
				suppressClick = true;
				// Settle on whoever is nearest the front.
				var best = 0;
				nodes.forEach(function (node, i) { if (Math.cos(node.a + rot) > Math.cos(nodes[best].a + rot)) { best = i; } });
				selected = -1;
				select(best);
				window.setTimeout(function () { suppressClick = false; }, 0);
			}
			stage.classList.remove('is-dragging');
			drag = null;
		};
		stage.addEventListener('pointerup', release);
		stage.addEventListener('pointercancel', release);
		stage.addEventListener('pointerleave', function () { engaged = false; });
		// Keyboard focus brings a person round; a mouse click's focus is left to the click handler.
		graph.addEventListener('focusin', function (e) {
			engaged = true;
			var i = indexOf(e.target);
			if (i >= 0 && e.target.matches(':focus-visible')) { select(i); }
		});
		graph.addEventListener('focusout', function () { engaged = false; });

		controls.addEventListener('click', function (e) {
			var stepButton = e.target.closest('[data-step]');
			if (stepButton) {
				select(selected + Number(stepButton.getAttribute('data-step')));
				return;
			}
			if (e.target.closest('.leaders-graph__toggle')) {
				stopped = !stopped;
				elapsed = 0;
				label();
				wake();
			}
		});

		if ('IntersectionObserver' in window) {
			new IntersectionObserver(function (entries) {
				onScreen = entries[0].isIntersecting;
				// The orbit spins out from the hub the first time it is seen.
				if (onScreen) {
					spreadTarget = 1;
					target = target || -nodes[0].a;
					wake();
				}
			}, { threshold: 0.25 }).observe(stage);
		} else {
			onScreen = true;
			spreadTarget = 1;
		}
		if (wide.addEventListener) { wide.addEventListener('change', layout); }
		var resizeTimer = 0;
		window.addEventListener('resize', function () {
			window.clearTimeout(resizeTimer);
			resizeTimer = window.setTimeout(function () {
				layout();
				wake();
			}, 150);
		});
		if (document.fonts && document.fonts.load) { document.fonts.load(FONT).then(function () { if (on) { draw(window.performance.now()); } }); }
		layout();
		wake();
	});
}());
